import React, {useEffect, useState, useMemo, useCallback, useRef} from 'react';
import {useParams, useSearchParams, Link as RouterLink} from 'react-router-dom';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Paper, CircularProgress, Grid, Chip, Button, Alert, Stack, Link, Tabs, Tab} from '@mui/material';
import ReconnectingEventSource from 'reconnecting-eventsource';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import ReplayIcon from '@mui/icons-material/Replay';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BlockIcon from '@mui/icons-material/Block';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {formatPlayerName, getStatusColor, getWinningColor} from "../../utils/formatters";
import {useTableUrlState} from '../../hooks/useTableUrlState';
import apiService from "../../services/apiService";
import MachineQueueCard from "../../components/MachineQueueCard";
import MachineDetailsCard from "../../components/machine/MachineDetailsCard";
import MachineCategoryCard from "../../components/machine/MachineCategoryCard";
import MachineTechProfileCard from "../../components/machine/MachineTechProfileCard";
import MachineCameraCard from "../../components/machine/MachineCameraCard";
import LogsCard from "../../components/LogsCard";
import Protected from "../../components/Protected";
import {useAuthDisabled} from "../../context/AuthDisabledContext";
import {ROLES} from "../../config/roles";

// --- Sub-Components ---
//
// Details / Category / Tech Profile / Camera are now reusable Pattern-B
// editable cards under components/machine/. Each owns its own per-card
// PUT endpoint + auth gating. See components/EditableCard.js for the
// shared shell and components/forms/ for the pure controlled inputs.

const ActionsCard = ({machineId}) => {
    const isDisabled = useAuthDisabled();
    const [sending, setSending] = useState(null);
    const fire = async (endpoint) => {
        setSending(endpoint);
        try {
            await apiService.post(`/api/machines/${machineId}/${endpoint}`);
        } catch (e) {
            console.error(e);
        } finally {
            setSending(null);
        }
    };
    const actions = [
        {key: 'status', icon: <InfoIcon/>, label: 'Status', color: 'info'},
        {key: 'game-result', icon: <ReplayIcon/>, label: 'Game Result', color: 'secondary'},
        {key: 'heartbeat-read', icon: <HeartBrokenIcon/>, label: 'Read Heartbeat', color: 'secondary'},
        {key: 'sound-alarm', icon: <NotificationsActiveIcon/>, label: 'Alarm', color: 'warning'},
        {key: 'heartbeat-shutdown', icon: <BlockIcon/>, label: 'Stop Heartbeat', color: 'error'},
        {key: 'restart-android', icon: <RestartAltIcon/>, label: 'Restart Android', color: 'error'},
    ];
    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Typography variant="h6" gutterBottom>Actions</Typography>
            <Grid container spacing={1}>
                {actions.map(({key, icon, label, color}) => (
                    <Grid size={{xs: 12, sm: "auto"}} key={key}>
                        <Button variant="contained" color={color} startIcon={icon} onClick={() => fire(key)}
                                disabled={isDisabled || !!sending}>
                            {label}
                        </Button>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
};

const LiveEventsCard = ({machineId}) => {
    const [events, setEvents] = useState([]);
    const [sseError, setSseError] = useState(null);
    const eventsContainerRef = useRef(null);

    useEffect(() => {
        setEvents([]);
        setSseError(null);
        const es = new ReconnectingEventSource(`/api/machines/${machineId}/events`, {
            withCredentials: true,
            max_retry_time: 1000
        });
        es.onopen = () => setSseError(null);
        es.onerror = () => setSseError('Live-updates connection lost; retrying…');
        const addEvent = (e) => setEvents(prev => [...prev.slice(-200), e.data]);
        es.addEventListener('message', addEvent);
        es.addEventListener('machine-event', addEvent);
        return () => {
            es.removeEventListener('message', addEvent);
            es.removeEventListener('machine-event', addEvent);
            es.close();
        };
    }, [machineId]);

    useEffect(() => {
        if (eventsContainerRef.current) {
            eventsContainerRef.current.scrollTop = eventsContainerRef.current.scrollHeight;
        }
    }, [events]);

    return (
        <Paper elevation={2} sx={{p: 2, height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Typography variant="h6" gutterBottom>Live Events</Typography>
            {sseError && <Typography color="error" variant="caption">{sseError}</Typography>}
            <Box
                ref={eventsContainerRef}
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    maxHeight: 300,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    mt: 1,
                    backgroundColor: 'background.default'
                }}
            >
                {events.map((ev, i) => <Typography key={i} variant="body2"
                                                   sx={{fontFamily: 'monospace'}}>{ev}</Typography>)}
            </Box>
        </Paper>
    );
};

const RecentGamesTable = ({machineId}) => {
    const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const {pagination, setPagination, sorting, setSorting} = useTableUrlState({
        initialSorting: [{id: 'startedAt', desc: true}], initialPageSize: 10
    });

    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({page: pagination.pageIndex, size: pagination.pageSize});
        if (sorting.length > 0) params.append('sort', `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}`);
        apiService.get(`/api/machines/${machineId}/games?${params.toString()}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [machineId, pagination, sorting]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'Game',
            size: 120,
            Cell: ({cell}) => <Link component={RouterLink}
                                    to={`/games/${cell.getValue()}`}>{cell.getValue().slice(0, 8)}…</Link>
        },
        {
            accessorFn: (row) => row.player?.name || row.player?.id,
            id: 'player',
            header: 'Player',
            Cell: ({row}) => <Link component={RouterLink}
                                   to={`/players/${row.original.player.id}`}>{formatPlayerName(row.original.player)}</Link>
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({cell}) => <Chip label={cell.getValue()} color={getStatusColor(cell.getValue())} size="small"/>
        },
        {
            accessorKey: 'winning',
            header: 'Result',
            Cell: ({cell}) => <Chip label={cell.getValue() || 'N/A'} color={getWinningColor(cell.getValue())}
                                    size="small"/>
        },
        {accessorKey: 'startedAt', header: 'Started', Cell: ({cell}) => new Date(cell.getValue()).toLocaleString()},
    ], []);

    const table = useMaterialReactTable({
        columns, data, manualPagination: true, manualSorting: true, rowCount,
        state: {pagination, sorting, isLoading, showAlertBanner: isError},
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        muiToolbarAlertBannerProps: isError ? {color: 'error', children: 'Error loading games'} : undefined,
    });

    return <MaterialReactTable table={table}/>;
};


// --- Main Page Component ---

// Tab is part of the URL (?tab=…) so reloads + shared links survive.
// useTableUrlState (inside RecentGamesTable) merges with — instead of
// overwriting — the existing URL params, so this co-exists with pagination.
const TABS = ['overview', 'games'];

const MachineDetailPage = () => {
    const {id} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const tab = TABS.includes(tabParam) ? tabParam : 'overview';
    const setTab = (next) => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', next);
        setSearchParams(params, {replace: true});
    };

    const [machine, setMachine] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [machineQueue, setMachineQueue] = useState([]);
    const [isQueueActionLoading, setQueueActionLoading] = useState(false);

    const fetchMachineQueue = useCallback(() => {
        apiService.get(`/api/queues/${id}`)
            .then(data => setMachineQueue(data))
            .catch(err => console.error("Failed to fetch machine queue:", err));
    }, [id]);

    const handleRemovePosition = async (machineId, position) => {
        setQueueActionLoading(true);
        try {
            await apiService.post(`/api/queues/${machineId}/remove`, position);
            fetchMachineQueue();
        } catch (err) {
            // Handle error, maybe with a snackbar
        } finally {
            setQueueActionLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        apiService.get(`/api/machines/${id}`)
            .then(data => setMachine(data))
            .catch(err => setError(err.message || err.toString() || 'Failed to load machine.'))
            .finally(() => setLoading(false));
    }, [id]);

    // Queue polling is only useful when the overview tab (which shows the
    // queue card) is visible — pause it on other tabs to avoid wasted calls.
    useEffect(() => {
        if (tab !== 'overview') return;
        fetchMachineQueue();
        const intervalId = setInterval(fetchMachineQueue, 2000);
        return () => clearInterval(intervalId);
    }, [tab, fetchMachineQueue]);

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;
    if (!machine) return <Alert severity="info" sx={{m: 2}}>No such machine.</Alert>;

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Machine #{id}</Typography>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{mb: 2, borderBottom: 1, borderColor: 'divider'}}
            >
                <Tab value="overview" label="Overview"/>
                <Tab value="games" label="Recent Games"/>
            </Tabs>

            {tab === 'overview' && (
                <Stack spacing={4}>
                    <Grid container spacing={4}>
                        <Grid size={{xs: 12, md: 3}}>
                            <MachineDetailsCard machine={machine} onUpdated={setMachine}/>
                        </Grid>
                        <Grid size={{xs: 12, md: 3}}>
                            <MachineCategoryCard machine={machine} onUpdated={setMachine}/>
                        </Grid>
                        <Grid size={{xs: 12, md: 3}}>
                            <MachineTechProfileCard machine={machine} onUpdated={setMachine}/>
                        </Grid>
                        <Grid size={{xs: 12, md: 3}}>
                            <Box sx={{height: '100%'}}>
                                <MachineQueueCard
                                    machineId={id}
                                    players={machineQueue}
                                    displayMode="compact"
                                    onRemovePosition={handleRemovePosition}
                                    isActionLoading={isQueueActionLoading}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    <MachineCameraCard machine={machine} onUpdated={setMachine}/>

                    <Protected component={MachineDetailPage} action="viewActions" mode="disable">
                        <ActionsCard machineId={id}/>
                    </Protected>
                    <LiveEventsCard machineId={id}/>
                    <LogsCard
                        title="Machine Logs"
                        streamUrl={`/api/machines/${id}/logs/stream`}
                        cacheKey={`machine:${id}`}
                        emptyText="No log entries mention this machineId."
                    />
                </Stack>
            )}

            {tab === 'games' && (
                <RecentGamesTable machineId={id}/>
            )}

            <Box sx={{mt: 3}}>
                <Button component={RouterLink} to="/machines" variant="outlined" startIcon={<ArrowBackIcon/>}>
                    Back to Machines
                </Button>
            </Box>
        </Box>
    );
};

MachineDetailPage.abilities = {
    read:             [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    viewActions:      [ROLES.SUPPORT, ROLES.ADMINS],
    performActions:   [ROLES.ADMINS],
    viewLiveEvents:   [ROLES.ADMINS],
    viewQueue:        [ROLES.SUPPORT, ROLES.ADMINS],
    manageQueue:      [ROLES.ADMINS],
    // viewTechProfile lives on MachineTechProfileCard.abilities.read now
    // (per-card abilities — the page no longer owns sub-card auth).
};

export default MachineDetailPage;